import React, { useEffect, useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntitiesSupportingStreetView, Lead } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import LeadItem from "@app/component/LeadItem";
import FeaturedMap, { Interactive, ToolButton, ZoomButtons } from "@app/component/map/FeaturedMap";
import EmbeddedModal from "@app/component/modal/EmbeddedModal";
import EntityCard from "@app/component/RecordCard/components/EntityCard";

import styles from "./StreetViewModal.module.scss";

interface Props {
  coordinates?: google.maps.LatLngLiteral;
  entity?: EntitiesSupportingStreetView;
  lead?: Lead;
  onHide: () => void;
  open: boolean;
}

const StreetViewModal = ({ coordinates, entity, lead, onHide, open }: Props) => {
  const [map, setMap] = useState<google.maps.Map | undefined>();
  const [streetViewUnavailable, setStreetViewUnavailable] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (map && coordinates) {
      const service = new google.maps.StreetViewService();
      service
        .getPanorama({ location: coordinates })
        .then(() => {
          const panorama = new google.maps.StreetViewPanorama(map.getDiv(), {
            disableDefaultUI: true,
            linksControl: true,
            panControl: true,
            position: coordinates,
            pov: {
              heading: 34,
              pitch: 10,
            },
          });
          map.setStreetView(panorama);
        })
        .catch(() => {
          setStreetViewUnavailable(true);
        });
    }
  }, [coordinates, map]);

  return (
    <EmbeddedModal onCancel={onHide} open={open}>
      <FeaturedMap
        className={styles.mapContainer}
        initialOptions={{
          controlSize: 32,
        }}
        mapClassName={styles.map}
        onInitialized={setMap}
      >
        <div className={cn({ [styles.streetViewBg]: streetViewUnavailable })} onClick={stopEvents}>
          <div className={styles.entityContainer}>
            {entity ? (
              <EntityCard
                className={styles.entityCard}
                entity={entity}
                showActions={false}
                showPinnedFields={false}
              />
            ) : lead ? (
              <LeadItem
                className={styles.entityCard}
                entity={lead}
                showActions={false}
                showSelectLead={false}
              />
            ) : null}
          </div>
          {streetViewUnavailable && (
            <div className={styles.alert}>
              <FontAwesomeIcon className={styles.icon} icon={faInfoCircle} size="2xl" />
              <span className={styles.alertTitle}>
                {intl.formatMessage({
                  id: "map.streetView.unavailable.title",
                  defaultMessage: "Street View is unavailable",
                  description: "Street View unavailable title",
                })}
              </span>
              <span className={styles.alertDescription}>
                {intl.formatMessage({
                  id: "map.streetView.unavailable.description",
                  defaultMessage:
                    "This can happen for a variety of reasons, like if Street View was never captured at this location. Please try again later in the case there is a connection issue or contact support.",
                  description: "Street View unavailable description",
                })}
              </span>
            </div>
          )}
          <Interactive>
            <div className={styles.closeIcon}>
              <ToolButton
                icon={faTimes}
                onClick={onHide}
                title={intl.formatMessage({
                  id: "map.streetView.controls.close",
                  defaultMessage: "Close",
                  description: "Close button for street view",
                })}
              />
            </div>
            <div className={styles.zoomButtons}>
              <ZoomButtons streetView />
            </div>
          </Interactive>
        </div>
      </FeaturedMap>
    </EmbeddedModal>
  );
};

export default StreetViewModal;
